/**
 * File: RemindersCard.tsx
 * Path: apps/admin/src/features/today/components/RemindersCard.tsx
 * Description: Reminders to send, on Today: the payment reminders due
 * today, each with its ready-made words and one button that sends it
 * (WhatsApp opens with the words typed, the firm email goes from the
 * server, a call is ticked off) and Dismiss. A reminder that can't go
 * (no phone, no email) says why and links to the client. Shows nothing
 * when there are none.
 */

import React from "react";
import { Link } from "react-router-dom";
import { formatCents } from "@idk/config";
import { Button } from "@idk/ui";
import { Box, Pill } from "@/shared/components/BooksPrimitive";
import { useRemindersController } from "../hooks/useRemindersController";

const linkClass =
  "inline-flex min-h-11 items-center font-bold hover:text-primary hover:underline";

export const RemindersCard: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { rows, pending, send, dismiss } = useRemindersController();
  if (rows.length === 0) return null;
  return (
    <Box
      title="Reminders to send"
      caption="Invoices due soon or late, with the words ready. Nothing goes to a client until you tap."
      className={className}
    >
      <ul
        aria-label="Reminders to send"
        className="divide-y divide-border/40 text-sm"
      >
        {rows.map((r) => (
          <li
            key={r.id}
            aria-label={`${r.party} ${r.invoiceNumber}`}
            className="flex flex-wrap items-start justify-between gap-stack-sm py-stack-xs"
          >
            <div className="min-w-0 flex-1 basis-60 space-y-stack-xs">
              <p className="flex flex-wrap items-center gap-x-stack-sm">
                <span className="font-bold">{r.party}</span>
                <Link to={r.invoiceHref} className={linkClass}>
                  {r.invoiceNumber}
                </Link>
              </p>
              <p className="text-xs text-muted-foreground">
                {r.what} · {formatCents(r.balanceCents)} owed
              </p>
              {r.words && (
                <details className="text-xs">
                  <summary className="inline-flex min-h-11 cursor-pointer items-center font-bold text-foreground">
                    The words
                  </summary>
                  {r.subject && <p className="font-bold">{r.subject}</p>}
                  <p className="whitespace-pre-line text-muted-foreground">
                    {r.words}
                  </p>
                </details>
              )}
              {r.blocker && (
                <div className="flex flex-wrap items-center gap-stack-xs">
                  <Pill tone="amber">Can't send yet</Pill>
                  <span className="text-xs text-foreground">{r.blocker}</span>
                  {r.clientHref && (
                    <Link
                      to={r.clientHref}
                      className="inline-flex min-h-11 items-center text-xs text-primary underline underline-offset-4"
                    >
                      Add it to the client
                    </Link>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-stack-xs">
              {r.whatsappHref && !r.blocker && !pending ? (
                <Button asChild className="h-11 rounded-none">
                  <a
                    href={r.whatsappHref}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => send(r)}
                  >
                    {r.action}
                    <span className="sr-only">
                      : {r.party}, {r.invoiceNumber}
                    </span>
                  </a>
                </Button>
              ) : (
                <Button
                  type="button"
                  className="h-11 rounded-none"
                  disabled={pending || Boolean(r.blocker)}
                  onClick={() => send(r)}
                >
                  {r.action}
                  <span className="sr-only">
                    : {r.party}, {r.invoiceNumber}
                  </span>
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-none"
                disabled={pending}
                onClick={() => dismiss(r)}
              >
                Dismiss
                <span className="sr-only">
                  : {r.party}, {r.invoiceNumber}
                </span>
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Box>
  );
};
